import React, { useCallback, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Typography, Box, Chip, Stack } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import dayjs from "dayjs";

import { usePaymentHistory } from "@/api/hooks/usePaymentHistory";
import { IPaymentHistoryParams } from "@/api/types/payments";
import { createData } from "../../helper";
import CustomerPaymentHistory from "../../paymentHistory";

import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";

const SubscriptionDetail: React.FC = () => {
    const { customerId, subscriptionId } = useParams<{ customerId: string, subscriptionId: string }>();

    // Get payment transactions for this subscription
    const [paymentParams, setPaymentParams] = useState<IPaymentHistoryParams>({
        skip: 0,
        take: 10,
        filter: {
            subscriptionId: Number(subscriptionId),
        },
    } as IPaymentHistoryParams);

    const { data: paymentsData, isFetching, isLoading } = usePaymentHistory(paymentParams);

    const handlePaymentChange = useCallback(({ page, rowsPerPage }: { page?: number, rowsPerPage?: number }) => {
        setPaymentParams((prev) => {
            return {
                ...prev,
                skip: Number(page) * Number(rowsPerPage),
                take: rowsPerPage || 10,
            };
        });
    }, []);

    // Get subscription details from the first payment (since all payments for this subscription will have the same subscription info)
    const payments = paymentsData?.data?.items || [];
    const firstPayment = payments[0];
    const subscription = firstPayment?.detail?.subscription;

    if (!subscription) {
        return (
            <>
                <DashboardHeader
                    title="Payment Not Found"
                    titleSuffix={
                        <Stack direction="row" spacing={2}>
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.SHOW.SHOW_BY_ID(Number(customerId)).ABSOLUTE}
                                startIcon={<ArrowBack />}
                                variant="outlined"
                            >
                                Back to Customer
                            </Button>
                        </Stack>
                    }
                />
                <DashboardContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                            textAlign: "center",
                            p: 4,
                        }}
                    >
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: "50%",
                                backgroundColor: "grey.100",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            <Typography variant="h2" color="grey.400">
                                !
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}>
                            Payment Not Found
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
                            The payment you&apos;re looking for doesn&apos;t exist or may have been removed. Please check the
                            payment ID and try again.
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.SHOW.SHOW_BY_ID(Number(customerId)).ABSOLUTE}
                                variant="contained"
                                startIcon={<ArrowBack />}
                            >
                                Back to Customer Details
                            </Button>
                        </Stack>
                    </Box>
                </DashboardContent>
            </>
        );
    }

    // Create subscription data from the payment's subscription object
    const subscriptionData = {
        id: Number(subscriptionId),
        planName: subscription.ml_plan_title?.en || "Unknown Plan",
        status: "ACTIVE", // Default status since it's from a successful payment
        startDate: subscription.startAt,
        endDate: subscription.endAt,
        paymentStatus: "success", // Since this is from a successful payment
        paymentDate: firstPayment?.createdAt || "",
        source: firstPayment?.source || "Unknown",
        autoRenewing: subscription.autoRenew === "Enabled",
        createdAt: firstPayment?.createdAt || "",
        updatedAt: firstPayment?.updatedAt || "",
    };
    const totalItems = paymentsData?.data?.pagination.totalItems;

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case "ACTIVE":
                return "success";
            case "EXPIRED":
                return "error";
            case "CANCELLED":
                return "warning";
            case "PENDING":
                return "info";
            default:
                return "default";
        }
    };

    return (
        <>
            <DashboardHeader
                title={`Subscription Details - ${subscriptionData.planName}`}
                titleSuffix={
                    <Stack direction="row" spacing={2}>
                        <Button
                            component={Link}
                            to={ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.SHOW.SHOW_BY_ID(Number(customerId)).ABSOLUTE}
                            startIcon={<ArrowBack />}
                            variant="outlined"
                        >
                            Back to Customer
                        </Button>
                    </Stack>
                }
            />
            <DashboardContent>
                {/* Subscription Details */}
                <Box
                    sx={{
                        mb: 4,
                        p: 3,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "text.primary" }}>
                        Subscription Information
                    </Typography>
                    <Stack spacing={2}>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Plan Name:</Typography>
                            <Typography>{subscriptionData.planName}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Status:</Typography>
                            <Chip
                                color={getStatusColor(subscriptionData.status)}
                                label={subscriptionData.status}
                                size="small"
                                variant="outlined"
                            />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Start Date:</Typography>
                            <Typography>{dayjs(subscriptionData.startDate).format("YYYY-MM-DD HH:mm")}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">End Date:</Typography>
                            <Typography>{dayjs(subscriptionData.endDate).format("YYYY-MM-DD HH:mm")}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Source:</Typography>
                            <Typography>{subscriptionData.source}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Auto Renewing:</Typography>
                            <Typography>{subscriptionData.autoRenewing ? "Yes" : "No"}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">Amount:</Typography>
                            <Typography>
                                {firstPayment?.amount} {firstPayment?.currency}
                            </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2">API ID:</Typography>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                {firstPayment?.apiId}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* Payment Transactions */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                        Payment Transactions for this Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View all payment attempts and transaction history for this subscription
                    </Typography>
                </Box>
                <CustomerPaymentHistory
                    data={payments.map((payment) => createData(payment))}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    page={Number(paymentParams.skip) / Number(paymentParams.take)}
                    rowsPerPage={Number(paymentParams.take)}
                    totalItems={totalItems || 0}
                    onChange={handlePaymentChange}
                />
            </DashboardContent>
        </>
    );
};

export default SubscriptionDetail;
